import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import type { DisclosuresReferencesInterface } from '../../../../../../types/ClientApplication';
import type { UploadedFiles } from '../../../../../../types/Shared';
import type { CCLFCharacterReferenceInterface } from '../../../../../Applications/CCLFForm/type';
import { PhoneNumberFormatDisplay } from '../../../../../UI/NumberFormatDisplay/PhoneNumberFormatDisplay';
import { ApplicationAttachments } from '../../../ApplicationAttachments';

export default function CCLFDisclosuresReferences({
  disclosuresReferences,
  files,
}: {
  disclosuresReferences: DisclosuresReferencesInterface;
  files: UploadedFiles[];
}) {
  const { id = 0 } = useParams();
  const queryClient = useQueryClient();
  const lawSuitFiles = files?.filter((file: UploadedFiles) => file.field === 'disclosuresReferences.disclosure.hasLawsuitsSupportingDoc');
  const defaultedOnLoanFiles = files?.filter(
    (file: UploadedFiles) => file.field === 'disclosuresReferences.disclosure.defaultedOnLoanSupportingDoc'
  );

  return (
    <section aria-labelledby='applicant-information-title'>
      <div className='bg-white shadow sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
            <h2 id='applicant-information-title' className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'>
              Disclosures and References
            </h2>
            <p className='mt-1 max-w-2xl text-sm text-gray-500'>Details of an application Disclosures & References.</p>
          </div>
        </div>
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6 space-y-5'>
          <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
            <div className='sm:col-span-2'>
              <dt className='text-sm font-medium text-gray-500'>Authorized Signer</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {disclosuresReferences?.disclosure.authorizedSigners.map(
                  (
                    signer: {
                      authorizedSigner: string;
                    },
                    idx: number
                  ) => (
                    <span key={idx}>{signer.authorizedSigner}, </span>
                  )
                )}
              </dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Bankruptcy/Lawsuit/Litigation</dt>
              <dd className='mt-1 text-sm text-gray-900 flex flex-col'>
                {disclosuresReferences?.disclosure.hasLawsuits ? <span>{disclosuresReferences.disclosure.hasLawsuitsExplain}</span> : 'No'}
              </dd>
              <dd className='mt-4'>
                <div className='col-span-2'>
                  <ApplicationAttachments
                    title='Supporting Documents'
                    files={lawSuitFiles}
                    field={'disclosuresReferences.disclosure.hasLawsuitsSupportingDoc'}
                    applicationId={id as number}
                    onUploadOrDeleteActionSuccess={() => {
                      queryClient.invalidateQueries(['get-client-application']);
                    }}
                  />
                </div>
              </dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Loan Defaulted</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {disclosuresReferences?.disclosure.defaultedOnLoan ? (
                  <span>{disclosuresReferences.disclosure.defaultLoanExplain}</span>
                ) : (
                  'No'
                )}
              </dd>
              <dd className='mt-4'>
                <div className='col-span-2'>
                  <ApplicationAttachments
                    title='Supporting Documents'
                    files={defaultedOnLoanFiles}
                    field={'disclosuresReferences.disclosure.defaultedOnLoanSupportingDoc'}
                    applicationId={id as number}
                    onUploadOrDeleteActionSuccess={() => {
                      queryClient.invalidateQueries(['get-client-application']);
                    }}
                  />
                </div>
              </dd>
            </div>
          </dl>
          <p className='text-md font-bold'>References</p>
          <dl className='grid grid-cols-2 col-span-2'>
            {disclosuresReferences.characterReference.map((ref: CCLFCharacterReferenceInterface, idx) => (
              <div key={idx}>
                <dt className='text-sm font-medium text-gray-500 capitalize'>{ref.name}</dt>
                <dd>{ref.relationship}</dd>
                <dd>
                  <PhoneNumberFormatDisplay value={ref.phone} />
                </dd>
                <dd>{ref.email}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
